import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../config/api';

const ManualPaymentVerification = ({ tripId, onVerificationSuccess }) => {
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Only show in development mode
  if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENV === 'production') {
    return null;
  }

  const handleVerify = async () => {
    if (!sessionId.trim()) {
      setError('Please enter a session ID');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      console.log('🚀 Manual verification for session:', sessionId);
      const response = await api.post('/payments/verify-payment', {
        sessionId: sessionId.trim()
      });

      console.log('✅ Manual verification response:', response.data);
      setResult(response.data);
      
      if (onVerificationSuccess) {
        onVerificationSuccess();
      }
    } catch (error) {
      console.error('❌ Manual verification error:', error);
      setError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoFillLatest = async () => {
    try {
      // Get the latest payment session for this trip
      const response = await api.get(`/payments/latest-session/${tripId}`);
      if (response.data.sessionId) {
        setSessionId(response.data.sessionId);
      } else {
        setError('No recent payment sessions found for this trip');
      }
    } catch (error) {
      setError('Could not fetch latest session');
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 2, border: '2px dashed #ff9800', bgcolor: '#fff3e0' }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#e65100' }}>
        🔧 Development Mode - Manual Payment Verification
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Since webhooks don't work in local development, manually verify your payment here:
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-end' }}>
        <TextField
          label="Stripe Session ID"
          placeholder="cs_test_..."
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          size="small"
          sx={{ flexGrow: 1 }}
          helperText="Enter the session ID from your payment"
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleAutoFillLatest}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <RefreshIcon fontSize="small" />
        </Button>
      </Box>

      <Button
        variant="contained"
        onClick={handleVerify}
        disabled={loading || !sessionId.trim()}
        sx={{ mb: 2 }}
      >
        {loading ? <CircularProgress size={20} /> : 'Verify Payment'}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ✅ {result.message}
          </Typography>
          <Typography variant="caption" display="block">
            Trip ID: {result.tripId}
          </Typography>
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        💡 Tip: Check browser console for the session ID after completing payment
      </Typography>
    </Paper>
  );
};

export default ManualPaymentVerification;
